"use client";

import React from "react";
import Image from "next/image";

export type PartnerCriterionItem = {
  title: string;
  icon: string; // path/URL to your custom icon image
};

export type ChoosePartnerProps = {
  title1?: React.ReactNode;
  title2?: React.ReactNode;
  description?: React.ReactNode;
  criteriaArray: PartnerCriterionItem[];
  noteText?: string;
};

function CheckBadge() {
  return (
    <span className="inline-flex items-center justify-center shrink-0 w-6 h-6 rounded-full bg-[#22b24c] text-white">
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M5 13l4 4L19 7"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function CriterionCard({ item }: { item: PartnerCriterionItem }) {
  return (
    <div className="group relative bg-white rounded-[20px] p-2 lg:p-3 flex items-center gap-4 border border-[#ecf0f6] transition-all duration-300 hover:-translate-y-1">
      {/* Icon */}
      <div className="relative shrink-0 w-24 h-24 rounded-[16px] bg-[#e9eef8] flex items-center justify-center overflow-hidden">
        <Image
          src={item.icon}
          alt={item.title}
          width={52}
          height={52}
          className="object-contain"
        />
      </div>

      {/* Title + Check */}
      <div className="flex flex-col justify-between gap-4">
        <h3 className="text-[16px] lg:text-[23px] font-medium leading-snug text-[#00040b]">
          {item.title}
        </h3>
        <CheckBadge />
      </div>
    </div>
  );
}

export default function ChoosePartner({
  title1 = "How To Choose The Right Mobile App Development Partner",
  title2 = (
    <>
      How To Choose The Right <span className="text-[#225bd4]">Mobile App</span>{" "}
      Development Partner
    </>
  ),
  description = "Choosing the right mobile app development company in USA includes evaluating relevant experience, security practices, long-term support, technical expertise, development capabilities, and communication. Also reviewing the company's existing portfolio, technology expertise, development methodology, and whether it also supports the applications beyond launch can help hire a reliable mobile app development company aligned to your unique business goals and needs.",
  criteriaArray,
  noteText = "Impact levels are indicative and vary by project.",
}: ChoosePartnerProps) {
  return (
    <section className="relative bg-[#d9e2ed] py-[80px] lg:py-[100px] overflow-hidden">
      <div className="relative z-[1] w-[calc(100%_-_10%)] mx-auto max-w-[2500px] flex flex-col gap-[50px]">
        {/* Header Section */}
        <div className="flex flex-col gap-4 ">
          <h2 className="clash-font font-medium leading-[120%] max-w-[1300px] text-[#00040b] text-[length:var(--section-title)]">
            {title2}
          </h2>
          <p className="text-[#00040b]/70 text-[length:var(--hero-para)] leading-[160%]">
            {description}
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {criteriaArray?.map((item, index) => (
            <CriterionCard key={index} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
}
